/**
 * `PhaseTimeline` — the horizontal step indicator above the two-column
 * agent/endpoint layout. Ported unchanged from the original monolithic
 * `Simulation.tsx` per ADR-0159 §6.
 */

import { Check, X } from 'lucide-react';

import { Card } from '../../../components/atoms/index.js';
import { useC } from '../../../lib/theme.js';
import { useIsMobile } from '../../../lib/use-is-mobile.js';
import type { Phase } from '../journeys/types.js';

const monoFont = "'JetBrains Mono', monospace";

export interface PhaseTimelineProps {
  phases: Phase[];
  step: number;
}

export function PhaseTimeline({ phases, step }: PhaseTimelineProps): JSX.Element {
  const C = useC();
  // ADR-0163 §6 — 9px kickers are house style for labels, but step
  // names on the primary teaching surface bump to 10px on narrow
  // viewports for legibility.
  const isMobile = useIsMobile();
  const labelSize = isMobile ? 10 : 9;
  return (
    <Card className="p-4">
      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto pb-1">
        {phases.map((p, i) => {
          const done = i < step;
          const active = i === step;
          const skipped = (p.skipped ?? false) && i <= step;
          const willHappen = i > step;
          const failed = active && !p.ok;
          return (
            <div key={p.id} className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <div className="flex flex-col items-center" style={{ minWidth: 60 }}>
                <div
                  className="flex items-center justify-center mb-1.5"
                  style={{
                    width: 24,
                    height: 24,
                    background: skipped ? C.surface2 : failed ? C.coralDim : active || done ? C.limeDim : C.surface2,
                    border: `1px solid ${skipped ? C.dim : failed ? C.coralFg : active || done ? C.limeFg : C.border}`,
                    borderRadius: 2,
                  }}
                >
                  {skipped ? (
                    <X size={11} color={C.dim} />
                  ) : failed ? (
                    <X size={11} color={C.coralFg} />
                  ) : active || done ? (
                    <Check size={11} color={C.limeFg} strokeWidth={3} />
                  ) : (
                    <span style={{ fontFamily: monoFont, fontSize: 9, color: C.dim }}>{i + 1}</span>
                  )}
                </div>
                <span
                  style={{ fontFamily: monoFont, fontSize: labelSize, color: skipped ? C.dim : willHappen ? C.muted : C.text, letterSpacing: 0.5, textAlign: 'center' }}
                >
                  {/* "you are here" affordance on the active step (ADR-0163 §6). */}
                  {active && <span style={{ color: C.limeFg, marginRight: 2 }}>‹</span>}
                  {p.name.toUpperCase()}
                </span>
              </div>
              {i < phases.length - 1 && <div className="flex-1 h-px min-w-[12px]" style={{ background: i < step ? C.limeFg : C.border }} />}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
